import PropTypes from 'prop-types';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';

import BlockRenderer from '@/global/BlockRenderer';

import { CtfTwoColumnContentStyle } from '@/utils/constants';

export function ContentColumn({
  assets,
  entries,
  fields,
  isFromTwoColumnContent = false,
  position,
  previousWidth,
  nextWidth,
  isBlock = false,
}) {
  const {
    id,
    width,
    background,
    sticky,
    content,
    verticallyAlignCentre,
    verticallyAlignSpaceBetween,
    additionalPadding,
    hasMobileYGap,
    backgroundImage,
  } = fields;
  const backgrounds = {
    default: 'bg-transparent',
    grey: 'bg-grey-1',
    'natural cream': 'bg-natural-cream',
  };
  const darkBackgrounds = {
    majesty: 'bg-warmer-grey',
    'progressive blue': 'bg-progressive-blue',
    'warm grey': 'bg-warm-grey',
  };
  const isDarkBackground = !!darkBackgrounds[background?.toLowerCase()];
  const bgColor = background
    ? backgrounds[background?.toLowerCase()] ||
      darkBackgrounds[background?.toLowerCase()]
    : null;
  const widthCols = {
    '25%': 'md:col-span-2',
    '37.5%': 'md:col-span-3',
    '50%': 'md:col-span-4',
    '62.5%': 'md:col-span-5',
    '75%': 'md:col-span-6',
    '100%': 'md:col-span-12',
  };
  const previousWidths = {
    '25%': 'lg:cols-start-7',
    '37.5%': 'lg:col-start-6',
    '50%': 'lg:col-start-5',
    '62.5%': 'lg:col-start-4',
    '75%': 'lg:cols-span-3',
    '100%': 'lg:col-start-1'
  };
  const compactText =
    (width === '50%' && nextWidth === '62.5%') ||
    (width === '62.5%' && nextWidth !== '37.5%');
  return (
    <div
      id={id}
      className={`aaco-column aaco-${position}-column lg:row-span-full dd-en:pb-20 dd-kr:pb-20 ${
        previousWidth && previousWidths[width]
          ? previousWidths[width]
          : 'lg:col-start-1'
      } ${
        width && widthCols[width] ? widthCols[width] : 'lg:col-span-4'
      } ${bgColor} ${
        (!background || background.toLowerCase() !== 'default' || sticky) &&
        'py-10 md:dd-en:py-[100px] md:dd-kr:py-[100px]'
      } ${
        !isBlock &&
        (!background || background.toLowerCase() !== 'default' || sticky) &&
        'mb-20'
      } ${verticallyAlignCentre && 'flex items-center'} ${
        verticallyAlignSpaceBetween && 'lg:flex lg:flex-col lg:justify-between'
      } ${
        sticky &&
        `md:sticky top-0 h-fit ${
          isDDEnv()
            ? 'md:dd-kr:rounded-r-[8px] md:dd-en:rounded-r-[8px]'
            : 'md:min-h-[100vh]'
        } pb-20`
      } ${additionalPadding && 'md:px-20'} ${
        hasMobileYGap && 'aaco-content-column--has-mobile-y-gap'
      }`}
      style={{
        backgroundImage: `url(${backgroundImage?.[0]?.original_secure_url})`,
      }}
    >
      <BlockRenderer
        blocks={content}
        columnLayout={!isBlock}
        assets={assets}
        entries={entries}
        compactText={compactText}
        hasBackground={isDarkBackground}
        isFromTwoColumnContent={isFromTwoColumnContent}
        isFromContentColumn
      />
    </div>
  );
}

function TwoColumnContent({ assets = [], entries = [], fields = null }) {
  let {
    leftColumn,
    rightColumn,
    name,
    additionalMarginBottom,
    hasBorderTop,
    hasBorderBottom,
    borderColor,
    type,
    backgroundColor,
    hideMobileBottomPadding,
  } = fields;

  leftColumn = leftColumn ? getContent(leftColumn.sys.id, entries) : undefined;
  rightColumn = rightColumn
    ? getContent(rightColumn.sys.id, entries)
    : undefined;

  const staticPrevWidths = {
    '50%': '50%',
    '37.5%': '62.5%',
    '62.5%': '37.5%',
    '25%': '25%',
    '75%': '75%',
  };

  const isRecipeOrRestaurant =
    name?.toLowerCase()?.startsWith('recipe') ||
    name?.toLowerCase()?.startsWith('restaurant');

  const borderCls = borderColor ? `border-1 border-${borderColor}` : '';
  const isDD = isDDEnv();
  const isWH = isWHEnv();
  return (
    <div
      className={`aaco-two-column-layout__wrapper flex flex-col ${
        type === CtfTwoColumnContentStyle.LEGAL ? 'aaco-two-column-legal' : ''
      } ${
        type === CtfTwoColumnContentStyle.RECIPE_DD
          ? 'aaco-two-column-recipe-dd'
          : ''
      } ${isWH ? `${hideMobileBottomPadding ? "pb-0 md:pb-10" : "pb-10"} ${backgroundColor !== undefined && `bg-${backgroundColor}`}` : ''}`}
    >
      {hasBorderTop && (
        <div className="container mx-auto mb-[28px]">
          <div className={`${borderCls} ${hasBorderTop && 'border-t'}`} />
        </div>
      )}
      <div
        className={`aaco-two-column-layout ${isWH ? "container mx-auto" : ""} grid grid-rows md:grid-cols-8 ${
          additionalMarginBottom && 'md:mb-20'
        } ${
          isDD &&
          type !== CtfTwoColumnContentStyle.LEGAL &&
          type !== CtfTwoColumnContentStyle.RECIPE_DD
            ? ''
            : type === CtfTwoColumnContentStyle.LEGAL ||
              type === CtfTwoColumnContentStyle.RECIPE_DD
            ? 'container mx-auto'
            : ''
        } ${isRecipeOrRestaurant ? 'md:container md:mx-auto' : ''}`}
        id={isRecipeOrRestaurant ? 'recipe-introduction' : name}
      >
        {leftColumn && (
          <ContentColumn
            entries={entries}
            assets={assets}
            fields={leftColumn.fields}
            nextWidth={rightColumn ? rightColumn.fields.width : null}
            position="left"
            isFromTwoColumnContent
          />
        )}
        {rightColumn && (
          <ContentColumn
            entries={entries}
            assets={assets}
            fields={rightColumn.fields}
            previousWidth={
              leftColumn
                ? leftColumn.fields.width
                : staticPrevWidths[rightColumn.fields.width]
            }
            position="right"
            isFromTwoColumnContent
          />
        )}
        {hasBorderBottom && (
          <div className="container mx-auto">
            <div className={`${borderCls} ${hasBorderBottom && 'border-b'}`} />
          </div>
        )}
      </div>
    </div>
  );
}

// @TODO: define real props for the Carousel based on it requirements
TwoColumnContent.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default TwoColumnContent;
